import { DateTimeRenderer } from "../../datetime/DateTimeFormatter";
import { EntitySidebarTile, EntitySidebarTileProps } from "./EntitySidebarTile/EntitySidebarTile";
import { NotAvailable, NotSet } from "../../misc/UIconstants";
import { GetPersons } from "../../misc/EntityRenders/EntityRenderer";
import {
  InventoryItem,
  InventoryItemFieldLabels,
  InventoryItemFilters,
  InventoryItemFiltersTranslator,
  InventoryItemSortingOptionsConsts,
  inventoryItemsConstants,
} from "../../../api/Inventories";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { CustomTypesVirtualizedSelectForm } from "../../forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { useForm } from "react-hook-form";
import { LucideIcon } from "../../icon/LucideIcon";
import { QuickAddInventoryItemForm } from "../../forms/QuickAdd/forms/QuickAddInventoryItemForm";
import { QuickAddWrapper } from "../../forms/QuickAdd/common/QuickAddWrapper";
import { inventoryItemsFiltersToForm } from "../../../Inventories/Table/InventoryItemsFilterBar";
import { RenderTabStoreHistoryButton } from "../../tables/Tabs/history/TabStoreHistory";
import { EntityStateIndicator } from "../../badges/EntityStateIndicator/EntityStateIndicator";
import { CustomTypeLabelRenderer } from "../../../Customization/CustomTypes/generics/CustomTypeRenderer";
import { defaultInventoriesFilters } from "../../../Inventories/Table/InventoriesTable";
import { defaultInventoryItemsFilters } from "../../../Inventories/Table/InventoryItemsTable";

interface InventoryTileProps extends EntitySidebarTileProps<InventoryItem, InventoryItemFilters> {
  showCustomTypeSelectWidget?: boolean;
  parentInventoryItem?: InventoryItem;
}

export const InventoriesTile = ({ showCustomTypeSelectWidget, ...props }: InventoryTileProps) => {
  const { control, watch } = useForm<Partial<InventoryItem>>();
  const [showCreateInventoryItemModal, setShowCreateInventoryItemModal] = useState<boolean>(false);

  const currentCustomType = watch("customType");

  const defaultControlsUnfolded = useCallback(
    ({
      filters,
      setFilters,
    }: {
      filters: InventoryItemFilters;
      setFilters: Dispatch<SetStateAction<InventoryItemFilters>>;
    }) => {
      if (!!props.controlsUnfolded || !showCustomTypeSelectWidget)
        return (
          <>
            <RenderTabStoreHistoryButton
              entityConstants={inventoryItemsConstants}
              filters={filters}
              filtersToForm={inventoryItemsFiltersToForm}
            />
            {props?.controlsUnfolded?.({ filters, setFilters })}
          </>
        );
      return (
        <div className="flex align-center gap-5" style={{ width: "100%" }}>
          <div style={{ height: "38px", overflow: "hidden", width: "100%", maxWidth: "30ch" }}>
            <CustomTypesVirtualizedSelectForm
              id="customType"
              filters={{
                ...defaultInventoriesFilters,
                parentTypeIds: props.parentInventoryItem?.customType?.id
                  ? [props.parentInventoryItem.customType.id]
                  : undefined,
                entityTypes: ["InventoryItem"],
              }}
              control={control}
              horizontal
              hasLabel={false}
              style={{ minWidth: "200px", padding: 0, width: "100%" }}
              placeholder={InventoryItemFieldLabels.customType}
            />
          </div>
          {!!props.showAddButton && (
            <>
              <button
                className="btn btn-ghost-primary flex align-center"
                onClick={() => setShowCreateInventoryItemModal(true)}
              >
                <LucideIcon name="plus" />
              </button>
              <QuickAddWrapper<InventoryItem>
                showModal={showCreateInventoryItemModal}
                setShowModal={setShowCreateInventoryItemModal}
                onCreate={() => setShowCreateInventoryItemModal(false)}
                onCancel={() => setShowCreateInventoryItemModal(false)}
              >
                {(quickAddProps) => (
                  <QuickAddInventoryItemForm
                    {...quickAddProps}
                    initialValues={{ customType: currentCustomType, parent: props.parentInventoryItem }}
                    customTypeFilters={{
                      parentTypeIds: props.parentInventoryItem?.customType
                        ? [props.parentInventoryItem.customType.id]
                        : undefined,
                    }}
                  />
                )}
              </QuickAddWrapper>
            </>
          )}
        </div>
      );
    },
    [control, currentCustomType, props, showCreateInventoryItemModal, showCustomTypeSelectWidget]
  );

  return (
    <EntitySidebarTile<InventoryItem, InventoryItemFilters>
      {...props}
      entityConstants={inventoryItemsConstants}
      orderByOptions={InventoryItemSortingOptionsConsts}
      translatorConsts={InventoryItemFiltersTranslator}
      defaultFilters={{
        ...defaultInventoryItemsFilters,
        ...props.defaultFilters,
        customTypeIds:
          props.defaultFilters?.customTypeIds ?? (currentCustomType?.id ? [currentCustomType.id] : undefined),
        childrenOfParentIds:
          props.defaultFilters?.childrenOfParentIds ??
          (props.parentInventoryItem?.id ? [props.parentInventoryItem.id] : undefined),
      }}
      excludeFilters={{
        ...props.excludeFilters,
        customTypeIds: () => !!props.defaultFilters?.customTypeIds || !!currentCustomType?.id,
        childrenOfParentIds: () => !!props.defaultFilters?.childrenOfParentIds,
      }}
      rowLabel={(e) => (
        <div className="ellipsisContainer">
          <span>{e.name || NotAvailable}</span>
          <EntityStateIndicator entityConstants={inventoryItemsConstants} entity={e} hideLabels />
        </div>
      )}
      rowDate={(e) => (
        <>{e.enteredOn ? <DateTimeRenderer date={e.enteredOn} includeElapsed={false} includeTime={false} /> : NotSet}</>
      )}
      // rowInfo={(e) => (
      //   <>
      //     {props.defaultFilters?.customTypeIds?.length !== 1 ? (
      //       e.customType?.name ?? NotSet
      //     ) : e.enteredBy ? (
      //       <GetPersons persons={e.enteredBy} createLinks={false} />
      //     ) : (
      //       NotSet
      //     )}
      //   </>
      // )}
      rowInfo={(e) => <>{e.enteredBy ? <GetPersons persons={e.enteredBy} createLinks={false} /> : NotSet}</>}
      rowId={(e) => <CustomTypeLabelRenderer entity={e} />}
      controlsUnfolded={defaultControlsUnfolded}
      showAddButton={!showCustomTypeSelectWidget && props.showAddButton}
    />
  );
};
